/**
 * Message Components
 *
 * Reusable components for rendering different types of chat messages.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import {
  parseMultipleChoice,
  isCompleteChoiceBlock,
} from "./parseMultipleChoice";
import { MultipleChoiceUI } from "./MultipleChoiceUI";

/**
 * Strip GCS signed URL image markdown from assistant text.
 * The LLM sometimes embeds ![alt](https://storage.googleapis.com/...) in its response,
 * but the image is already displayed in the tool message. These signed URLs expire after
 * 1 hour, so we remove them to prevent broken images.
 */
function stripGcsImageMarkdown(text: string): string {
  // Match markdown images pointing to GCS signed URLs: ![alt](https://storage.googleapis.com/...)
  return text.replace(
    /!\[[^\]]*\]\(https?:\/\/storage\.googleapis\.com\/[^)]+\)/g,
    ""
  ).trim();
}

export interface Message {
  id: string;
  role: "user" | "assistant" | "system" | "tool" | "developer";
  content: string;
  timestamp: Date;
  imageUrls?: string[];
  /** Thinking/reasoning content from models with extended thinking (e.g. Claude) */
  thinkingContent?: string;
  /** Duration of the thinking phase in seconds */
  thinkingDuration?: number;
}

export interface MessageItemProps {
  message: Message;
  isStreaming?: boolean;
  /** Callback when user selects a multiple choice answer */
  onChoiceSelect?: (choiceId: string, selectedLabels: string[]) => void;
  /** Set of choice IDs that have already been answered */
  answeredChoices?: Map<string, string>;
  /** Active pending choices for sequential reveal (only for this message) */
  activePendingChoices?: {
    messageId: string;
    choices: Array<{ id: string; question: string; allowMultiple: boolean }>;
    currentIndex: number;
    collectedAnswers: Map<string, string[]>;
  } | null;
}

interface TodoItem {
  id: string;
  content: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
}

interface ToolDisplayContent {
  title: string;
  toolName?: string;
  content?: { output: any; figurePlans?: any; content?: any; claims?: any };
  rawOutput?: any;
  message?: string;
  timestamp?: string;
  immediate?: boolean;
  callId?: string;
  todos?: TodoItem[];
  type: "call" | "result" | "unknown";
}

/**
 * Parse and format tool message content
 */
const getToolDisplayContent = (content: string): ToolDisplayContent => {
  try {
    const parsed = JSON.parse(content);
    const action = parsed.action || "unknown";

    if (action === "tool_call" || action === "tool_start_immediate") {
      return {
        title: `${parsed.toolName || "Tool"} Called`,
        toolName: parsed.toolName,
        message: parsed.message,
        timestamp: parsed.timestamp,
        immediate: parsed.immediate,
        callId: parsed.callId,
        type: "call" as const,
      };
    } else if (action === "tool_result") {
      // Parse the actual tool output
      let toolOutput;
      try {
        toolOutput =
          typeof parsed.output === "string"
            ? JSON.parse(parsed.output)
            : parsed.output;
      } catch {
        toolOutput = parsed.output;
      }

      // Extract todos array — could be at the top level of parsed or inside toolOutput
      const todos: TodoItem[] | undefined =
        parsed.todos || toolOutput?.todos || undefined;

      return {
        title: `✅ ${parsed.toolName || "Tool"} Result`,
        toolName: parsed.toolName,
        content: toolOutput,
        rawOutput: parsed.output,
        // Check both toolOutput.message (old DocumentAgent format) and
        // parsed.message (TipTap/LangGraph agent format) for the summary text
        message: toolOutput?.message || parsed.message,
        timestamp: parsed.timestamp,
        immediate: parsed.immediate,
        callId: parsed.callId,
        todos,
        type: "result" as const,
      };
    }

    return {
      title: "🤖 Tool Message",
      content: { output: content },
      type: "unknown" as const,
    };
  } catch (e) {
    return {
      title: "🤖 Tool Message",
      content: { output: content },
      type: "unknown" as const,
    };
  }
};

/**
 * Collapsible display for write_todos tool results.
 * Shows task items with status icons in a compact, expandable format.
 */
const TodoListDisplay: React.FC<{ todos: TodoItem[]; summary: string }> = ({
  todos,
  summary,
}) => {
  const [expanded, setExpanded] = useState(true);

  const statusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <span className="todo-status-icon todo-completed">✓</span>;
      case "in_progress":
        return <span className="todo-status-icon todo-in-progress">●</span>;
      case "cancelled":
        return <span className="todo-status-icon todo-cancelled">✕</span>;
      default:
        return <span className="todo-status-icon todo-pending">○</span>;
    }
  };

  return (
    <div className="todo-list-display">
      <button
        className="todo-list-toggle"
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <span className={`todo-chevron ${expanded ? "expanded" : ""}`}>▶</span>
        <span className="todo-summary-text">{summary}</span>
      </button>
      {expanded && (
        <ul className="todo-items-list">
          {todos.map((item, idx) => (
            <li
              key={item.id || idx}
              className={`todo-item todo-item-${item.status}`}
            >
              {statusIcon(item.status)}
              <span className="todo-item-content">{item.content}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Component for rendering tool messages.
 *
 * Tool messages go through two states:
 * 1. Loading (type="call"): shows a spinner + translated tool name (e.g. "Searching documents...")
 * 2. Result (type="result"): shows the concise result summary (e.g. 'Search "X": 12 matches')
 *
 * When callId is present the loading message is updated in-place via the
 * meta_ prefix convention, so the same DOM element transitions smoothly.
 */
const ToolMessage: React.FC<{ message: Message }> = ({ message }) => {
  const { t } = useTranslation();
  const toolData = getToolDisplayContent(message.content);

  // Loading state — spinner + translated tool name
  if (toolData.type === "call") {
    const toolKey = toolData.toolName || "default";
    const loadingText = t(`toolNames.${toolKey}`, t("toolNames.default"));

    return (
      <div className="patent-draft-agent-message tool-message">
        <div className="patent-draft-agent-message-container">
          <div className="patent-draft-agent-text tool-message-text">
            <div className="tool-header tool-loading">
              <span className="tool-spinner" />
              {loadingText}...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Result state — concise summary (with optional collapsible todo list)
  const hasTodos =
    toolData.toolName === "write_todos" &&
    Array.isArray(toolData.todos) &&
    toolData.todos.length > 0;

  return (
    <div className="patent-draft-agent-message tool-message">
      <div className="patent-draft-agent-message-container">
        <div className="patent-draft-agent-text tool-message-text">
          {hasTodos ? (
            <TodoListDisplay
              todos={toolData.todos!}
              summary={toolData.message || "Task plan"}
            />
          ) : (
            <div className="tool-header">
              {toolData.message ?? <strong>{toolData.title}</strong>}
            </div>
          )}
          {Array.isArray(message.imageUrls) &&
            message.imageUrls.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                {message.imageUrls.map((url, idx) => (
                  <a key={idx} href={url} target="_blank" rel="noreferrer">
                    <img
                      src={url}
                      crossOrigin="use-credentials"
                      alt={`Document attachment ${idx + 1}`}
                      onError={(e) => {
                        // Replace broken image with a placeholder
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector(".img-error-placeholder")) {
                          const placeholder = document.createElement("div");
                          placeholder.className = "img-error-placeholder";
                          placeholder.style.cssText =
                            "width:200px;height:80px;display:flex;align-items:center;justify-content:center;" +
                            "border-radius:6px;border:1px solid #dee2e6;background:#f8f9fa;color:#6c757d;font-size:12px;text-align:center;padding:8px;";
                          placeholder.textContent = "Image unavailable — file may not exist in storage";
                          parent.appendChild(placeholder);
                        }
                      }}
                      style={{
                        maxWidth: 200,
                        maxHeight: 200,
                        objectFit: "contain",
                        borderRadius: 6,
                        border: "1px solid #dee2e6",
                        backgroundColor: "#fff",
                      }}
                    />
                  </a>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

/**
 * Collapsible group for consecutive tool messages of the same type.
 * Collapsed by default to reduce chat clutter (like Cursor's tool grouping).
 */
export const CollapsedToolGroup: React.FC<{
  toolName: string;
  messages: Message[];
}> = ({ toolName, messages }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const toolKey = toolName || "default";
  const displayName = t(`toolNames.${toolKey}`, toolName);

  return (
    <div className="patent-draft-agent-message tool-message">
      <div className="patent-draft-agent-message-container">
        <div className="patent-draft-agent-text tool-message-text">
          <button
            className="tool-group-toggle"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
          >
            <span className={`todo-chevron ${expanded ? "expanded" : ""}`}>
              ▶
            </span>
            <span className="tool-group-summary">
              {displayName} ({messages.length} calls)
            </span>
          </button>
          {expanded && (
            <div className="tool-group-items">
              {messages.map(msg => {
                const data = getToolDisplayContent(msg.content);
                return (
                  <div key={msg.id} className="tool-group-item">
                    {data.message || data.title}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Component for rendering system messages using the same visual style as tool messages
 */
const SystemMessage: React.FC<{ message: Message }> = ({ message }) => {
  const { t } = useTranslation();
  const hasPurchaseLink = message.content.includes("{{PURCHASE_LINK}}");
  const displayContent = hasPurchaseLink
    ? message.content.replace(" {{PURCHASE_LINK}}", "")
    : message.content;

  return (
    <div className="patent-draft-agent-message tool-message">
      <div className="patent-draft-agent-message-container">
        <div className="patent-draft-agent-text tool-message-text">
          <div className="tool-header">
            {displayContent}
            {hasPurchaseLink && (
              <>
                {" "}
                <Link to="/payment" style={{ color: "#0d6efd" }}>
                  {t("displayspecificworktypepage.purchaseMoreTokens")}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component for rendering user messages (with markdown support)
 */
const UserMessage: React.FC<{ message: Message }> = ({ message }) => (
  <div className="patent-draft-agent-message user-message">
    <div className="patent-draft-agent-message-container">
      <div className="patent-draft-agent-text">
        <div className="user-message-text">{message.content}</div>
        {Array.isArray(message.imageUrls) && message.imageUrls.length > 0 && (
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}
          >
            {message.imageUrls.map((url, idx) => (
              <a key={idx} href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  crossOrigin="use-credentials"
                  alt={`user-img-${idx}`}
                  style={{
                    width: 96,
                    height: 96,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #dee2e6",
                  }}
                />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

/**
 * Component for rendering assistant messages with markdown support
 * - While streaming: render plain text for responsiveness
 * - After streaming: render markdown (GFM) with sanitization
 * - Parses and renders multiple choice blocks as interactive UI with sequential reveal
 */
const AssistantMessage: React.FC<{
  message: Message;
  isStreaming?: boolean;
  onChoiceSelect?: (choiceId: string, selectedLabels: string[]) => void;
  answeredChoices?: Map<string, string>;
  activePendingChoices?: {
    messageId: string;
    choices: Array<{ id: string; question: string; allowMultiple: boolean }>;
    currentIndex: number;
    collectedAnswers: Map<string, string[]>;
  } | null;
}> = ({ message, isStreaming, onChoiceSelect, answeredChoices, activePendingChoices }) => {
  // During streaming, don't try to parse incomplete multiple choice blocks
  const shouldParse = !isStreaming || isCompleteChoiceBlock(message.content);
  const segments = shouldParse
    ? parseMultipleChoice(message.content)
    : [{ type: "text" as const, content: message.content }];

  // Determine if we should show only the current question (sequential reveal)
  const showSequential = activePendingChoices !== null && activePendingChoices !== undefined && activePendingChoices.messageId === message.id;
  const currentChoiceId = showSequential && activePendingChoices
    ? activePendingChoices.choices[activePendingChoices.currentIndex]?.id
    : null;

  return (
    <div className="patent-draft-agent-message assistant-message">
      <div className="patent-draft-agent-message-container">
        <div className="patent-draft-agent-text">
          {/* Render all text segments first, then choice segments — ensures
              the AI's explanation always appears above the multiple-choice UI
              regardless of the order the LLM generated them. */}
          {segments
            .filter(s => s.type === "text")
            .map((segment, index) => {
              const sanitizedContent = stripGcsImageMarkdown(segment.content as string);
              if (!sanitizedContent) return null;
              return (
                <div key={`text-${index}`} className="assistant-markdown-text">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSanitize]}
                  >
                    {sanitizedContent}
                  </ReactMarkdown>
                </div>
              );
            })}
          {segments
            .filter(s => s.type === "choice")
            .map((segment) => {
              const choiceData = segment.content as any;
              const isAnswered = answeredChoices?.has(choiceData.id) ?? false;
              const answeredValue = answeredChoices?.get(choiceData.id);

              // Sequential reveal: only show current question or already answered ones
              if (showSequential && !isAnswered && choiceData.id !== currentChoiceId) {
                return null;
              }

              return (
                <MultipleChoiceUI
                  key={`choice-${choiceData.id}`}
                  data={choiceData}
                  isAnswered={isAnswered}
                  answeredValue={answeredValue}
                  onSelect={onChoiceSelect || (() => {})}
                  autoSubmit={!choiceData.allowMultiple}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

/**
 * Main message item component that routes to appropriate sub-component
 */
export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isStreaming,
  onChoiceSelect,
  answeredChoices,
  activePendingChoices,
}) => {
  const isUser = message.role === "user";
  const isTool = message.role === "tool";
  const isSystem = message.role === "system";

  if (isTool) {
    return <ToolMessage message={message} />;
  }

  if (isSystem) {
    return <SystemMessage message={message} />;
  }

  if (isUser) {
    return <UserMessage message={message} />;
  }

  return (
    <AssistantMessage
      message={message}
      isStreaming={isStreaming}
      onChoiceSelect={onChoiceSelect}
      answeredChoices={answeredChoices}
      activePendingChoices={activePendingChoices}
    />
  );
};

/**
 * Loading indicator component for streaming messages
 */
export const LoadingIndicator: React.FC = () => (
  <div className="patent-draft-agent-loading-indicator">
    <div className="patent-draft-agent-message assistant-message">
      <div className="patent-draft-agent-message-container">
        <div className="patent-draft-agent-text">
          <div className="loading-dots">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Welcome message component
 */
export const WelcomeMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="patent-draft-agent-welcome-message">
      <p>{t("documentAgentChat.welcomeLine1")}</p>
      <p>{t("documentAgentChat.welcomeLine2")}</p>
    </div>
  );
};
